// The 3-5-8 table (.ai/358.md): Kierki's table (hearts/HeartsTable.tsx, its classes and flights) for 3 seats. On a
// computer the classic table (the 2 computer players' backs left and right, the trick as a "T" on the field, the musik
// above it, the hand fanned at the bottom); on a phone the compact one (the opponents as tiles, big cards in rows). The
// declarer names the game in a panel over the field, seeing only his first 6 cards; the exchange flies each card to
// its taker and the answer back; the musik turns up for all, flies to the declarer, and the 4 cards he lays away fly
// back to its place, face down. The end of a deal opens the score window (the Kierki score window's look). Tap or
// click only; the root keeps the `.ht` class, so ui/tapClick.ts clicks its taps on the finger's lift (iPad).
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { t } from '../i18n';
import { QuickSay, SayBubble } from '../online/QuickSay';
import { NextDealNote } from '../online/NextDealNote';
import { SeatAvatar, seatFlag as seatFlagOf } from '../online/SeatAvatar';
import { flagged } from '../online/PlayerName';
import { SeatWait, TurnClock } from '../online/TurnClock';
import { mpStore, useMultiplayerState } from '../multiplayer/store';
import { backSrc, backSrcSet, faceSrc, faceSrcSet } from '../options/assets';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { useController } from '../store/gameStore';
import { isShortGame, reloadForNewGame } from '../hearts/nextGame';
import { AdSlot } from '../windows/AdSlot';
import { WindowFrame } from '../windows/WindowFrame';
import { useFitWindow } from '../windows/useFitWindow';
import { CARD_RATIO, Face, SHADOW, backPic, centre, facePic, flightShadow, ghost, usePhone, whenLanded } from '../hearts/HeartsTable';
import { CrownIcon, LeadIcon, SuitIcon, withSuits } from '../hearts/suits';
import { fixedNames } from '../hearts/names';
import {
  CONTRACTS, available, cardKey, leaders, legalCards, playerWon, sameCard, targetOf, trumpOf, winScore, type Contract, type HCard, type Played,
  type State358,
} from './rules';
import { COLLECT_MS, DEAL_FLIGHT_MS, DEAL_STEP_MS, MUSIK_FLY_MS, SWAP_FLY_MS, store358, use358, type TableState } from './store';
import '../hearts/hearts.css';
import '../makao/makao.css';
import './t58.css';

/** A sentence with each real player's flag beside the name (owner 2026-11-01); not the hint over the hand, which has the
 *  suits only (owner 2026-11-02: "usuń stąd flagę użytkownika"). */
const flaggedText = (text: string) => flagged(text, store358().getState().names);

const SYM = { C: '♣', D: '♦', S: '♠', H: '♥' } as const;
const FLY_MS = 340;
const CLOSE_MS = 450;

export const seatName = (seat: number): string => (seat === 0 ? t('hearts.you') : (store358().getState().names ?? fixedNames(t)).name[seat]!);
/** Online: the seat's flag badge (a real player), else null (online/SeatAvatar.tsx). */
const seatFlag = (seat: number): string | null => seatFlagOf(store358().getState().names, seat);
/** The game's name: "Atu ♥", "Bez atu", "Bez lew". */
export const contractName = (c: Contract): string => (c === 'NT' ? t('t58.contract.NT') : c === 'NL' ? t('t58.contract.NL') : t('t58.contract.trump', { suit: SYM[c] }));

/** New game after a finished game: a full reload (the ads refresh); after a game under 2 minutes the next one at once
 *  (hearts/nextGame.ts). */
function useNewGame(): () => void {
  const ctrl = useController();
  const { startedAt, endedAt } = use358();
  return () => (isShortGame(startedAt, endedAt) ? void ctrl.newGame() : reloadForNewGame('three-five-eight'));
}

/** Where the player's cards and the musik's were on screen at the last render: a card that left flies from there. */
const handRects = new Map<string, DOMRect>();
const musikRects: DOMRect[] = [];
const seatBox = (seat: number): DOMRect | null => document.querySelector(`.t58 [data-src="${seat}"]`)?.getBoundingClientRect() ?? null;
const musikBox = (): DOMRect | null => document.querySelector('.t58 .t58-musik')?.getBoundingClientRect() ?? null;
const box = (r: DOMRect, w: number) => {
  const c = centre(r), h = w * CARD_RATIO;
  return new DOMRect(c.x - w / 2, c.y - h / 2, w, h);
};
const cardW = () => document.querySelector('.t58 .ht-hand .ht-card')?.getBoundingClientRect().width ?? 70;

/** What a tap on a card of the hand does now (null: nothing). */
function tapMode(g: State358): 'give' | 'back' | 'lay' | 'play' | null {
  if (g.phase === 'exchange' && g.pendingTrump?.taker === 0) return 'back';
  if (g.phase === 'exchange' && !g.pendingTrump && g.swaps[0]?.giver === 0) return 'give';
  if (g.phase === 'musik' && !g.musik.length && g.declarer === 0) return 'lay';
  if (g.phase === 'play' && g.turn === 0) return 'play';
  return null;
}

/** The player's hand. While the player declares only the first 6 cards show (the other 10 lie face down at the right);
 *  the cards chosen to lay away lift; the cards not allowed dim. */
function Hand({ g, st, deck, back }: { g: State358; st: TableState; deck: string; back: string }) {
  const store = store358();
  const mode = tapMode(g);
  const legal = mode === 'play' ? legalCards(g, 0) : null;
  const ref = useRef<HTMLDivElement>(null);
  const placed = useRef(new Map<string, { x: number; y: number }>());
  const hidden = g.phase === 'declare' && g.declarer === 0;
  const wasHidden = useRef(false);
  useLayoutEffect(() => {
    const hand = ref.current;
    if (!hand) return;
    const now = new Map<string, { x: number; y: number }>();
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) {
      now.set(el.dataset.card!, { x: el.offsetLeft, y: el.offsetTop });
      handRects.set(el.dataset.card!, el.getBoundingClientRect());
    }
    const before = placed.current;
    placed.current = now;
    const revealed = wasHidden.current && !hidden;
    wasHidden.current = hidden;
    if (!before.size || typeof hand.animate !== 'function' || hand.closest('[data-dealing]')) return;
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) {
      const was = before.get(el.dataset.card!), is = now.get(el.dataset.card!)!;
      // the 10 hidden cards turn over where they now lie (the game was named)
      if (!was && revealed) {
        el.querySelector('.ht-face')?.animate([{ transform: 'scaleX(0)' }, { transform: 'none' }], { duration: 320, delay: 80, easing: 'ease-out', fill: 'backwards' });
        continue;
      }
      if (!was || (Math.abs(was.x - is.x) < 0.5 && Math.abs(was.y - is.y) < 0.5)) continue;
      el.animate([{ transform: `translate(${was.x - is.x}px, ${was.y - is.y}px)` }, { transform: 'none' }], { duration: CLOSE_MS, easing: 'ease-in-out' });
    }
  });
  const cards: Array<HCard | null> = hidden ? [...g.firstSix, ...Array.from({ length: g.hands[0]!.length - g.firstSix.length }, () => null)] : g.hands[0]!.slice();
  const n = cards.length;
  return (
    <div ref={ref} className="ht-hand" data-count={n} style={{ '--n': n, '--step': Math.min(0.5, 7.2 / Math.max(1, n - 1)), '--cols': n > 16 ? 10 : 8 } as CSSProperties}>
      {cards.map((c, i) => {
        if (!c)
          return (
            <span key={`b${i}`} className="ht-card t58-hidden" data-card={`b${i}`} aria-hidden="true">
              <img className="ht-face" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} />
            </span>
          );
        const sel = st.selected.some((s) => sameCard(s, c));
        const can = mode !== null && (legal === null || legal.some((x) => sameCard(x, c))) && !(mode === 'lay' && !sel && st.selected.length >= 4);
        return (
          <button
            key={cardKey(c)}
            type="button"
            className="ht-card"
            data-card={cardKey(c)}
            data-selected={sel || undefined}
            data-off={mode !== null && !can ? '' : undefined}
            onClick={(e) => {
              if (!can) return;
              handRects.set(cardKey(c), e.currentTarget.getBoundingClientRect());
              store.tap(c);
            }}
          >
            <span className="ht-sh" aria-hidden="true" />
            <Face card={c} deck={deck} />
          </button>
        );
      })}
    </div>
  );
}

/** A card of the trick: it flies in from its player (FLIP). */
function TrickCard({ p, g, deck, className }: { p: Played; g: State358; deck: string; className: string }) {
  const fly = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = fly.current;
    if (!el || typeof el.animate !== 'function') return;
    const to = el.getBoundingClientRect();
    const from = p.seat === 0 ? (handRects.get(cardKey(p.card)) ?? seatBox(0)) : seatBox(p.seat);
    if (!from || !to.width) return;
    const a = centre(from), b = centre(to);
    const scale = p.seat === 0 ? from.width / to.width : 0.6;
    el.animate([{ transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${scale})` }, { transform: 'none' }], { duration: FLY_MS, easing: 'cubic-bezier(.2,.75,.3,1)' });
    flightShadow(el, SHADOW.arc, { duration: FLY_MS });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, when the card arrives
  return (
    <div className={`ht-trick__slot ${className}`} data-seat={p.seat} data-card={cardKey(p.card)} data-winner={g.phase === 'trick-done' && g.winner === p.seat ? '' : undefined}>
      <div ref={fly} className="ht-trick__fly">
        <span className="ht-sh" aria-hidden="true" />
        <Face card={p.card} deck={deck} />
      </div>
    </div>
  );
}

/** The musik's place above the trick: face down until the game is named and the exchange is over, then face up for
 *  everyone; empty once taken, then the 4 cards laid away, face down. */
function Musik({ g, deck, back }: { g: State358; deck: string; back: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const cards = ref.current?.querySelectorAll('.t58-musik__card');
    if (!cards?.length) return;
    musikRects.length = 0;
    cards.forEach((el) => musikRects.push(el.getBoundingClientRect()));
  });
  const up = g.phase === 'musik' && g.musik.length > 0;
  const laid = g.discards.length > 0;
  const shown = g.musik.length ? g.musik : laid ? g.discards : [];
  return (
    <div ref={ref} className="t58-musik" data-up={up || undefined} data-laid={laid || undefined} aria-label={t('t58.musik')}>
      {shown.length
        ? shown.map((c, i) =>
            up ? (
              <div key={cardKey(c)} className="t58-musik__card" data-card={cardKey(c)}>
                <Face card={c} deck={deck} />
              </div>
            ) : (
              <div key={`m${i}`} className="t58-musik__card">
                <img className="ht-face" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} />
              </div>
            ),
          )
        : null}
    </div>
  );
}

/** The game's badge on the field: what is trumps (or no trumps / no tricks), and who plays it. */
function ContractBadge({ g }: { g: State358 }) {
  if (!g.contract) return null;
  return (
    <div className="t58-contract">
      <span className="mk-badge">{withSuits(contractName(g.contract))}</span>
    </div>
  );
}

/** The declarer's choice: the 6 games, the ones already played greyed out. */
function Picker({ g }: { g: State358 }) {
  if (g.phase !== 'declare' || g.declarer !== 0) return null;
  const free = available(g, 0);
  return (
    <div className="mk-pick t58-pick" role="dialog" aria-label={t('t58.pick.title')}>
      <b>{t('t58.pick.title')}</b>
      <div className="mk-pick__row">
        {CONTRACTS.slice(0, 4).map((c) => (
          <button key={c} type="button" className="mk-pick__btn mk-pick__suit" data-contract={c} data-suit={c} disabled={!free.includes(c)} onClick={() => store358().choose(c)}>
            <SuitIcon s={c as 'S'} />
          </button>
        ))}
      </div>
      <div className="mk-pick__row">
        {(['NT', 'NL'] as const).map((c) => (
          <button key={c} type="button" className="mk-pick__btn mk-pick__btn--wide" data-contract={c} disabled={!free.includes(c)} onClick={() => store358().choose(c)}>
            {contractName(c)}
          </button>
        ))}
      </div>
      <small>{t('t58.pick.note')}</small>
    </div>
  );
}

/** A card as text for a hint: "7♠", "K♥" (the suit becomes the game's icon through withSuits). */
const cardText = (c: HCard) => `${({ 11: 'J', 12: 'Q', 13: 'K', 14: 'A' } as Record<number, string>)[c.r] ?? c.r}${SYM[c.s]}`;
/** How long the hint says what an exchange of the player's brought (owner 2026-10-28: a 7♠ given, a 2♣ back, and
 *  nothing at the table said why: spades were trumps). */
const SWAP_NOTE_MS = 2600;
/** The line that tells what the player's last exchange did, for a moment after it (null: none). */
function useSwapNote(st: TableState): string | null {
  // the exchange whose note has run out (the note is worked out from the store; the timer only ends it)
  const [expired, setExpired] = useState(0);
  useEffect(() => {
    if (!st.swap) return;
    const id = setTimeout(() => setExpired(st.swap), SWAP_NOTE_MS);
    return () => clearTimeout(id);
  }, [st.swap]);
  const d = st.lastSwap;
  const g = st.game;
  if (!st.swap || st.swap === expired || !d || !g || (d.giver !== 0 && d.taker !== 0)) return null;
  const trump = trumpOf(g.contract);
  const p = { gave: cardText(d.gave), got: cardText(d.got), name: seatName(d.giver === 0 ? d.taker : d.giver) };
  if (sameCard(d.gave, d.got)) return t(d.giver === 0 ? 't58.swap.back' : 't58.swap.backTheirs', p);
  if (d.giver === 0) return t(d.gave.s === trump ? 't58.swap.mineTrump' : 't58.swap.mine', p);
  return t('t58.swap.theirs', p);
}

function hint(g: State358, st: TableState): string {
  if (g.phase === 'declare') return g.declarer === 0 ? t('t58.hint.declare') : t('t58.hint.declaring', { name: seatName(g.declarer) });
  if (g.phase === 'exchange') {
    const p = g.pendingTrump;
    if (p) return p.taker === 0 ? t('t58.hint.giveBack', { name: seatName(p.giver) }) : t('t58.hint.swap', { a: seatName(p.giver), b: seatName(p.taker) });
    const r = g.swaps[0]!;
    if (r.giver === 0) return t('t58.hint.give', { name: seatName(r.taker), n: r.left, trump: SYM[trumpOf(g.contract)!] });
    return t('t58.hint.swap', { a: seatName(r.giver), b: seatName(r.taker) });
  }
  if (g.phase === 'musik') {
    if (g.musik.length) return t('t58.hint.musik', { name: seatName(g.declarer) });
    return g.declarer === 0 ? t('t58.hint.lay', { n: st.selected.length }) : t('t58.hint.laying', { name: seatName(g.declarer) });
  }
  if (g.phase === 'trick-done') return t('hearts.hint.trick', { name: seatName(g.winner) });
  if (g.phase !== 'play') return '';
  if (g.turn !== 0) return t('hearts.hint.turn', { name: seatName(g.turn) });
  if (!g.trick.length) return t('t58.hint.lead');
  const led = g.trick[0]!.card.s;
  if (g.hands[0]!.some((c) => c.s === led)) return t('hearts.hint.follow', { suit: SYM[led] });
  const trump = trumpOf(g.contract);
  return trump && g.hands[0]!.some((c) => c.s === trump) ? t('t58.hint.trump', { suit: SYM[led], trump: SYM[trump] }) : t('hearts.hint.void', { suit: SYM[led] });
}

/** The buttons by the hint: no more giving, lay the 4 away, the next deal / a new game with the window closed. */
function Actions({ g, st }: { g: State358; st: TableState }) {
  const store = store358();
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  if (g.phase === 'hand-end' || g.phase === 'over') {
    // online the next deal comes by itself and the score window holds the end's choices (a new game here would reload)
    if (online || uiStore().isOpen('t58_score')) return null;
    return (
      <button type="button" className="ht-go" id="t58_again" onClick={() => (g.phase === 'over' ? newGame() : store.nextHand())}>
        {t(g.phase === 'over' ? 'hearts.score.newGame' : 'hearts.score.next')}
      </button>
    );
  }
  if (g.phase === 'exchange' && !g.pendingTrump && g.swaps[0]?.giver === 0)
    return (
      <button type="button" className="ht-go mk-go--soft" id="t58_stop" onClick={() => store.stopGiving()}>
        {t('t58.button.stop')}
      </button>
    );
  if (g.phase === 'musik' && !g.musik.length && g.declarer === 0)
    return (
      <button type="button" className="ht-go" id="t58_discard" disabled={st.selected.length !== 4} onClick={() => store.discard()}>
        {t('t58.button.lay')}
      </button>
    );
  return null;
}

const turnOf = (g: State358, seat: number) => g.phase === 'play' && g.turn === seat;

/** What a seat has in this deal: its tricks out of its target once the game is named, else its part (deals, plays). */
function dealLine(g: State358, seat: number): string {
  if (g.contract && g.phase !== 'declare') return t(g.contract === 'NL' ? 't58.tricksMax' : 't58.tricks', { n: g.tricks[seat]!, of: targetOf(g, seat) });
  if (seat === g.declarer) return t('t58.role.declarer');
  if (seat === g.dealer) return t('t58.role.dealer');
  return '';
}

function Plate({ g, seat, src }: { g: State358; seat: number; src?: boolean }) {
  const line = dealLine(g, seat);
  return (
    <div className="ht-plate" data-seat={seat} data-turn={turnOf(g, seat) || undefined} data-src={src ? seat : undefined} data-declarer={seat === g.declarer || undefined}>
      <SeatAvatar name={seatName(seat)} flag={seatFlag(seat)} />
      <SeatWait seat={seat} />
      <div className="ht-plate__text">
        <b>{seatName(seat)}</b>
        <span>
          {t('t58.points', { n: g.scores[seat]! })}
          {line ? ` · ${line}` : ''}
        </span>
      </div>
    </div>
  );
}

function Backs({ g, seat, back }: { g: State358; seat: number; back: string }) {
  return (
    <div className="ht-backs" data-src={seat} aria-hidden="true">
      {g.hands[seat]!.map((_, i) => (
        <img key={i} className="ht-back" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} />
      ))}
    </div>
  );
}

/** A: the classic table (a computer). */
function ClassicTable({ g, st, deck, back }: { g: State358; st: TableState; deck: string; back: string }) {
  const note = useSwapNote(st);
  return (
    <div className="ht-a t58-a">
      <div className="ht-seat ht-seat--left">
        <Plate g={g} seat={1} />
        <Backs g={g} seat={1} back={back} />
      </div>
      <div className="ht-seat ht-seat--right">
        <Plate g={g} seat={2} />
        <Backs g={g} seat={2} back={back} />
      </div>
      <div className="ht-mid">
        <div className="ht-felt" aria-hidden="true" />
        <Musik g={g} deck={deck} back={back} />
        <ContractBadge g={g} />
        <div className="ht-trick ht-trick--cross">
          {g.trick.map((p) => (
            <TrickCard key={`${g.handNo}-${cardKey(p.card)}`} p={p} g={g} deck={deck} className={`ht-trick__slot--s${p.seat === 2 ? 3 : p.seat}`} />
          ))}
        </div>
        <Picker g={g} />
      </div>
      <div className="ht-hintrow">
        <span className="ht-hint" data-hint="">
          {withSuits(note ?? hint(g, st))}
          <TurnClock at={st.deadlineAt} />
        </span>
        <Actions g={g} st={st} />
      </div>
      <div className="ht-me">
        <Plate g={g} seat={0} src />
      </div>
      <Hand g={g} st={st} deck={deck} back={back} />
    </div>
  );
}

/** C: compact, big cards (a phone). */
function CompactTable({ g, st, deck, back }: { g: State358; st: TableState; deck: string; back: string }) {
  const note = useSwapNote(st);
  const start = g.trick.length ? g.trick[0]!.seat : g.turn >= 0 ? g.turn : g.declarer;
  const order = [0, 1, 2].map((k) => (start + k) % 3);
  const line0 = dealLine(g, 0);
  return (
    <div className="ht-c t58-c">
      <div className="ht-chips">
        {[1, 2].map((p) => {
          const line = dealLine(g, p);
          return (
            <div key={p} className="ht-chip" data-seat={p} data-turn={turnOf(g, p) || undefined} data-src={p} data-declarer={p === g.declarer || undefined}>
              <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
              <SeatWait seat={p} />
              <b>{seatName(p)}</b>
              <span>{t('t58.points', { n: g.scores[p]! })}</span>
              <small>{line || ' '}</small>
            </div>
          );
        })}
      </div>
      <div className="t58-c-mid">
        <div className="t58-c-top">
          <Musik g={g} deck={deck} back={back} />
          <ContractBadge g={g} />
        </div>
        <div className="ht-trick ht-trick--row">
          {order.map((p, k) => {
            const played = g.trick.find((x) => x.seat === p);
            return (
              <div key={p} className="ht-place" data-lead={(g.phase === 'play' || g.phase === 'trick-done') && k === 0 ? '' : undefined}>
                <span>
                  {/* a phone: the name alone over its place, no flag (owner 2026-11-02: "na mobilkach flagi nie renderuj, aby zwolnić miejsce") */}
                  {seatName(p)}
                  {(g.phase === 'play' || g.phase === 'trick-done') && k === 0 ? <LeadIcon /> : null}
                </span>
                {played ? <TrickCard key={`${g.handNo}-${cardKey(played.card)}`} p={played} g={g} deck={deck} className="" /> : <div className="ht-place__empty" />}
              </div>
            );
          })}
        </div>
        <Picker g={g} />
      </div>
      {/* a slot of two lines: a longer or shorter hint no longer moves the table above (owner 2026-11-02) */}
      <div className="ht-msgslot">
        <div className="ht-msg">
          <span className="ht-hint" data-hint="">
            {withSuits(note ?? hint(g, st))}
            <TurnClock at={st.deadlineAt} />
          </span>
          <Actions g={g} st={st} />
        </div>
      </div>
      <div className="ht-mechip" data-seat={0} data-turn={turnOf(g, 0) || undefined} data-src={0} data-declarer={g.declarer === 0 || undefined}>
        {seatName(0)} · {t('t58.points', { n: g.scores[0]! })}
        {line0 ? ` · ${line0}` : ''}
      </div>
      <Hand g={g} st={st} deck={deck} back={back} />
    </div>
  );
}

/** The deal: a face-down deck in the middle; 52 cards thrown one after the other round the table from the declarer,
 *  4 of them to the musik (never the first or the last, never two in a row, as the rules ask of a dealer). The
 *  player's cards fly themselves; the computer players' backs and the musik as copies above the table. */
const MUSIK_AT = [11, 24, 33, 46];
function useDealAnimation(deal: number, g: State358 | null, back: string): void {
  useLayoutEffect(() => {
    const root = document.querySelector<HTMLElement>('.t58');
    const mid = document.querySelector<HTMLElement>('.t58 .ht-felt, .t58 .ht-trick--row');
    if (!deal || !g || !root || !mid || typeof root.animate !== 'function') return;
    const from = centre(mid.getBoundingClientRect());
    const ghosts: HTMLElement[] = [];
    const flights: Animation[] = [];
    const deckW = cardW() * 0.9, deckH = deckW * CARD_RATIO;
    const deckBox = new DOMRect(from.x - deckW / 2, from.y - deckH / 2, deckW, deckH);
    const deckEl = ghost(deckBox, backPic(back), ghosts);
    deckEl.style.zIndex = '40';
    const deckGone = deckEl.animate([{ opacity: 1 }, { opacity: 1, offset: 0.97 }, { opacity: 0 }], { duration: 51 * DEAL_STEP_MS + 60, fill: 'forwards' });
    void deckGone.finished.then(() => deckEl.remove(), () => deckEl.remove());
    flights.push(deckGone);
    // the order of the throws: seat by seat from the declarer, the musik's 4 at their steps
    const steps: Array<{ seat: number | 'm'; i: number }> = [];
    const count = [0, 0, 0];
    let m = 0;
    for (let k = 0; k < 52; k++) {
      if (MUSIK_AT.includes(k)) steps.push({ seat: 'm', i: m++ });
      else {
        const seat = (g.declarer + (k - m)) % 3;
        steps.push({ seat, i: count[seat]!++ });
      }
    }
    const at = (seat: number | 'm', i: number) => steps.findIndex((s) => s.seat === seat && s.i === i);
    const throwTo = (to: DOMRect, order: number, vanish: boolean) => {
      const c = centre(to);
      const el = ghost(new DOMRect(c.x - deckW / 2, c.y - deckH / 2, deckW, deckH), backPic(back), ghosts);
      const timing = { duration: DEAL_FLIGHT_MS, delay: order * DEAL_STEP_MS };
      const f = el.animate([{ transform: `translate(${from.x - c.x}px, ${from.y - c.y}px)`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: `scale(${to.width / deckW})`, opacity: vanish ? 0 : 1 }], { ...timing, easing: 'cubic-bezier(.45,.05,.35,1)', fill: 'backwards' });
      flightShadow(el, SHADOW.arc, timing);
      flights.push(f);
      void f.finished.then(() => el.remove(), () => el.remove());
    };
    // the player's cards: in the hand's order (a dealt hand is sorted as it is picked up)
    root.querySelectorAll<HTMLElement>('.ht-hand .ht-card').forEach((el, i) => {
      const to = el.getBoundingClientRect(), b = centre(to);
      const timing = { duration: DEAL_FLIGHT_MS, delay: at(0, i) * DEAL_STEP_MS };
      flights.push(el.animate([{ transform: `translate(${from.x - b.x}px, ${from.y - b.y}px) scale(${deckW / to.width})`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: 'none', opacity: 1 }], { ...timing, easing: 'cubic-bezier(.45,.05,.35,1)', fill: 'backwards' }));
      flightShadow(el, SHADOW.arc, timing);
    });
    for (const seat of [1, 2]) {
      const backs = [...root.querySelectorAll<HTMLElement>(`.ht-backs[data-src="${seat}"] .ht-back`)];
      if (backs.length) {
        backs.forEach((el, i) => {
          throwTo(el.getBoundingClientRect(), at(seat, i), false);
          flights.push(el.animate([{ opacity: 0 }, { opacity: 0 }], { duration: at(seat, i) * DEAL_STEP_MS + DEAL_FLIGHT_MS }));
        });
        continue;
      }
      const tile = seatBox(seat);
      if (tile) for (let i = 0; i < 16; i++) throwTo(box(tile, 40), at(seat, i), true);
    }
    root.querySelectorAll<HTMLElement>('.t58-musik__card').forEach((el, i) => {
      throwTo(el.getBoundingClientRect(), at('m', i), false);
      flights.push(el.animate([{ opacity: 0 }, { opacity: 0 }], { duration: at('m', i) * DEAL_STEP_MS + DEAL_FLIGHT_MS }));
    });
    root.dataset.dealing = '';
    let over = false;
    const end = () => {
      if (over) return;
      over = true;
      delete root.dataset.dealing;
    };
    const cancel = whenLanded(flights, 51 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 1000, end);
    return () => {
      cancel();
      end();
      for (const el of ghosts) el.remove();
    };
  }, [deal]); // eslint-disable-line react-hooks/exhaustive-deps -- one per deal
}

/** A copy flying from `from` to `to` above the table (shrinking to `to`'s size). */
function fly(from: DOMRect, to: DOMRect, pic: ReturnType<typeof backPic>, delay: number, duration: number, vanish: boolean, ghosts: HTMLElement[]) {
  const w = Math.max(from.width, to.width);
  const a = centre(from), b = centre(to);
  const el = ghost(box(from, w), pic, ghosts);
  el.style.opacity = '0';
  const timing = { duration, delay };
  const f = el.animate([{ transform: `scale(${from.width / w})`, opacity: 1 }, { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px) scale(${to.width / w})`, opacity: vanish ? 0 : 1 }], { ...timing, easing: 'cubic-bezier(.3,.6,.3,1)', fill: 'forwards' });
  flightShadow(el, SHADOW.arc, timing);
  void f.finished.then(() => el.remove(), () => el.remove());
}

/** A card of the player's hand that has just come: it flies in from `from` (FLIP), turning face up. */
function flyIn(el: HTMLElement, from: DOMRect, delay: number, duration: number) {
  const to = el.getBoundingClientRect(), a = centre(from), b = centre(to);
  const timing = { duration, delay };
  el.animate([{ transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${from.width / to.width})`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: 'none', opacity: 1 }], { ...timing, easing: 'cubic-bezier(.3,.6,.3,1)', fill: 'backwards' });
  flightShadow(el, SHADOW.arc, timing);
}

/** The exchange's two cards: the given one to its taker, the answer back (face up where the player sees it). */
function useSwapFlights(swap: number, st: TableState, deck: string, back: string): void {
  useLayoutEffect(() => {
    const done = st.lastSwap;
    const root = document.querySelector<HTMLElement>('.t58');
    if (!swap || !done || !root || typeof root.animate !== 'function') return;
    const ghosts: HTMLElement[] = [];
    const seen = done.giver === 0 || done.taker === 0;
    const pic = (c: HCard) => (seen ? facePic(deck, c) : backPic(back));
    const small = cardW() * 0.6;
    const handCard = (c: HCard) => root.querySelector<HTMLElement>(`.ht-hand .ht-card[data-card="${cardKey(c)}"]`);
    const leg = (c: HCard, from: number, to: number, delay: number) => {
      const fromBox = from === 0 ? handRects.get(cardKey(c)) ?? seatBox(0) : seatBox(from);
      if (!fromBox) return;
      const inHand = to === 0 ? handCard(c) : null;
      if (inHand) return flyIn(inHand, from === 0 ? fromBox : box(fromBox, small), delay, SWAP_FLY_MS);
      const toBox = seatBox(to);
      if (toBox) fly(from === 0 ? fromBox : box(fromBox, small), box(toBox, small), pic(c), delay, SWAP_FLY_MS, true, ghosts);
    };
    // the answer comes back once the given card has landed (the same card when the taker had none of its suit)
    leg(done.gave, done.giver, done.taker, 0);
    leg(done.got, done.taker, done.giver, SWAP_FLY_MS + 80);
    return () => {
      for (const el of ghosts) el.remove();
    };
  }, [swap]); // eslint-disable-line react-hooks/exhaustive-deps -- one per exchange
}

/** The musik flies to the declarer; the 4 laid away fly from him to its place, face down. */
// the copies remove themselves on landing
function useMusikFlights(g: State358 | null, deck: string, back: string): void {
  const seen = useRef<{ musik: number; discards: number; deal: number } | null>(null);
  useLayoutEffect(() => {
    if (!g) return;
    const before = seen.current;
    seen.current = { musik: g.musik.length, discards: g.discards.length, deal: g.handNo };
    const root = document.querySelector<HTMLElement>('.t58');
    if (!before || before.deal !== g.handNo || !root || typeof root.animate !== 'function') return;
    const ghosts: HTMLElement[] = [];
    if (before.musik && !g.musik.length) {
      const rects = musikRects.slice();
      g.musikCards.forEach((c, i) => {
        const from = rects[i] ?? musikBox();
        if (!from) return;
        const el = g.declarer === 0 ? root.querySelector<HTMLElement>(`.ht-hand .ht-card[data-card="${cardKey(c)}"]`) : null;
        if (el) return flyIn(el, from, i * 90, MUSIK_FLY_MS);
        const to = seatBox(g.declarer);
        if (to) fly(from, box(to, from.width * 0.7), facePic(deck, c), i * 90, MUSIK_FLY_MS, true, ghosts);
      });
    }
    if (!before.discards && g.discards.length) {
      const places = [...root.querySelectorAll<HTMLElement>('.t58-musik__card')];
      g.discards.forEach((c, i) => {
        const from = g.declarer === 0 ? handRects.get(cardKey(c)) : seatBox(g.declarer);
        const place = places[i];
        if (!from || !place) return;
        const to = place.getBoundingClientRect();
        place.animate([{ opacity: 0 }, { opacity: 0 }], { duration: i * 90 + MUSIK_FLY_MS });
        fly(g.declarer === 0 ? from : box(from, to.width), to, backPic(back), i * 90, MUSIK_FLY_MS, false, ghosts);
      });
    }
  });
}

/** The full trick slides to its taker (the store's `collecting`). */
function useCollect(g: State358 | null, collecting: boolean) {
  useLayoutEffect(() => {
    if (!collecting || !g || g.winner < 0) return;
    const to = g.winner === 0 ? (document.querySelector('.t58 .ht-hand')?.getBoundingClientRect() ?? seatBox(0)) : seatBox(g.winner);
    if (!to) return;
    const b = centre(to);
    for (const el of document.querySelectorAll<HTMLElement>('.t58 .ht-trick__fly')) {
      if (typeof el.animate !== 'function') continue;
      const a = centre(el.getBoundingClientRect());
      el.animate([{ transform: 'none', opacity: 1 }, { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px) scale(0.45)`, opacity: 0 }], { duration: COLLECT_MS, easing: 'ease-in', fill: 'forwards' });
    }
  }, [collecting]); // eslint-disable-line react-hooks/exhaustive-deps -- one per trick
}

export function Table358() {
  const st = use358();
  const g = st.game;
  const skin = useUiState().options.skin;
  const phone = usePhone();
  useDealAnimation(st.deal, g, skin.cardBackType);
  useSwapFlights(st.swap, st, skin.deckType, skin.cardBackType);
  useMusikFlights(g, skin.deckType, skin.cardBackType);
  useCollect(g, st.collecting);
  return (
    <div id="main" className="board ht t58" data-board="" data-game="three-five-eight" data-layout={phone ? 'compact' : 'classic'} data-phase={g?.phase} data-deck={skin.deckType}>
      {g ? phone ? <CompactTable g={g} st={st} deck={skin.deckType} back={skin.cardBackType} /> : <ClassicTable g={g} st={st} deck={skin.deckType} back={skin.cardBackType} /> : null}
    </div>
  );
}

/** The hero of the score window: three, five and eight. */
const HERO = ['3h', '5c', '8d', '5s', '3d'];
const FIT_STEPS = 6;

/** The end of a deal or of the game: the game played, each seat's tricks against its target, the points of the deal,
 *  the totals with a crown on the highest. */
export function ScoreWindow358({ instance }: { instance: WindowInstance }) {
  const st = use358();
  const g = st.game;
  const skin = useUiState().options.skin;
  useFitWindow('.lbx_window.lbx_t58_score', FIT_STEPS, g ? `${g.handNo}-${g.phase}` : null);
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  if (!g) return null;
  const over = g.phase === 'over';
  const best = leaders(g.scores);
  const youWin = playerWon(g);
  const last = g.history[g.history.length - 1];
  const go = () => {
    if (over) return newGame();
    uiStore().closeWindow('t58_score');
    store358().nextHand();
  };
  const title = over ? (youWin ? t('hearts.score.youWinMost') : t('hearts.score.game')) : t('t58.score.deal', { n: g.handNo + 1, of: g.length });
  const sub = last ? t('t58.score.played', { name: seatName(last.declarer), game: contractName(last.contract) }) : '';
  return (
    <WindowFrame name="t58_score" header={t(over ? 'hearts.score.game' : 'hearts.score.hand')} overlay closing={instance.closing}>
      <div className="tab_contents">
        <div className="hs-backdrop" aria-hidden="true" style={{ '--hs-back': `url("${backSrc(skin.cardBackType)}")` } as CSSProperties} />
        <div className="hs-cont">
          <div className="hs-hero" aria-hidden="true">
            {HERO.map((id, i) => (
              <img key={id} className="hs-hero__card" src={faceSrc(skin.deckType, id)} srcSet={faceSrcSet(skin.deckType, id)} alt="" draggable={false} style={{ '--i': i - 2 } as CSSProperties} />
            ))}
          </div>
          <div className="hs-title" data-win={youWin || undefined}>
            {title}
          </div>
          <div className="hs-sub">{flaggedText(over && !youWin ? t('hearts.score.winnerMost', { name: best.map(seatName).join(', ') }) : sub)}</div>
          <div className="hs-tiles t58-tiles">
            {[0, 1, 2].map((p) => {
              const pts = last?.points[p] ?? 0;
              return (
                <div key={p} className="hs-tile" data-me={p === 0 || undefined} data-best={best.includes(p) || undefined}>
                  <SayBubble seat={p} />
                  <div className="hs-tile__who">
                    <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
                    <b>{seatName(p)}</b>
                    {best.includes(p) ? <CrownIcon /> : null}
                  </div>
                  <div className="hs-tile__deal" data-zero={pts === 0 || undefined} data-minus={pts < 0 || undefined}>
                    {pts > 0 ? `+${pts}` : String(pts)}
                  </div>
                  <div className="hs-tile__total">{g.scores[p]}</div>
                  <small>{last ? t(last.contract === 'NL' ? 't58.tricksMax' : 't58.tricks', { n: last.tricks[p]!, of: last.targets[p]! }) : ''}</small>
                </div>
              );
            })}
          </div>
          <p className="hs-legend">{over && youWin && !online ? t('t58.score.win', { n: winScore(g) }) : t('t58.score.legend')}</p>
          {online ? (over ? <QuickSay iWon={youWin} /> : <QuickSay key={g.handNo} iWon={false} deal />) : null}
        </div>
        <AdSlot slot={2} />
        {online ? (
          // online: the next deal comes by itself; after the game, the same table again or leave it (as Kierki)
          <div className="rules_buttons hs-buttons">
            {over ? (
              <>
                <input type="button" className="mp_btn_secondary" id="mp_cards_leave" value={t('lobby.room.leave')} onClick={() => mpStore().leaveCards()} />
                <input type="button" className="nomoves_shuffle" id="mp_cards_again" value={t('lobby.again')} onClick={() => mpStore().again()} />
              </>
            ) : (
              <NextDealNote />
            )}
          </div>
        ) : (
          <div className="rules_buttons hs-buttons">
            <input type="button" className="nomoves_shuffle" id="t58_next" value={t(over ? 'hearts.score.newGame' : 'hearts.score.next')} onClick={go} />
          </div>
        )}
      </div>
    </WindowFrame>
  );
}
